<template>
  <div class="gl-page">
    <!-- Page Header -->
    <div class="gl-page-header">
      <div class="gl-page-header-left">
        <h1 class="gl-page-title">Promo Codes</h1>
        <p class="gl-page-sub">Manage discount codes and promotions</p>
      </div>
      <div class="gl-page-header-right">
        <router-link to="/super-admin/promos/create" class="gl-btn-primary">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Promo
        </router-link>
      </div>
    </div>

    <!-- Skeleton Stats -->
    <div v-if="loading" class="gl-stats-grid">
      <div v-for="i in 4" :key="i" class="gl-stat-card">
        <div class="gl-stat-icon-wrap" style="background:#f0f3f2"></div>
        <div style="flex:1">
          <div class="gl-skel gl-skel-sm mb-2"></div>
          <div class="gl-skel gl-skel-lg" style="height:28px"></div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div v-else-if="stats" class="gl-stats-grid">
      <div class="gl-stat-card">
        <div class="gl-stat-icon-wrap" style="background:#e8f4f1">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#900C27" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
        </div>
        <div class="gl-stat-body">
          <div class="gl-stat-label">Total Promos</div>
          <div class="gl-stat-value">{{ stats.total || 0 }}</div>
        </div>
      </div>
      <div class="gl-stat-card">
        <div class="gl-stat-icon-wrap" style="background:#e6f7f0">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <div class="gl-stat-body">
          <div class="gl-stat-label">Active Codes</div>
          <div class="gl-stat-value" style="color:#16a34a">{{ stats.active || 0 }}</div>
        </div>
      </div>
      <div class="gl-stat-card">
        <div class="gl-stat-icon-wrap" style="background:#e8f0fd">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        </div>
        <div class="gl-stat-body">
          <div class="gl-stat-label">Total Uses</div>
          <div class="gl-stat-value" style="color:#2563eb">{{ stats.total_uses || 0 }}</div>
        </div>
      </div>
      <div class="gl-stat-card">
        <div class="gl-stat-icon-wrap" style="background:#fef3e2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
        </div>
        <div class="gl-stat-body">
          <div class="gl-stat-label">Total Discount Given</div>
          <div class="gl-stat-value" style="color:#d97706">RM {{ formatCurrency(stats.total_discount) }}</div>
        </div>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="gl-filter-bar">
      <div class="gl-filter-search">
        <svg class="gl-filter-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input class="gl-filter-input" type="text" v-model="filters.search" placeholder="Search by code or name…" @input="debouncedFetch">
      </div>
      <select class="gl-filter-select" v-model="filters.status" @change="fetchPromos">
        <option value="">All Status</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
        <option value="expired">Expired</option>
        <option value="scheduled">Scheduled</option>
      </select>
      <select class="gl-filter-select" v-model="filters.discount_type" @change="fetchPromos">
        <option value="">All Types</option>
        <option value="percentage">Percentage</option>
        <option value="fixed">Fixed Amount</option>
      </select>
      <button class="gl-filter-clear" @click="resetFilters">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        Clear
      </button>
    </div>

    <!-- Table Card -->
    <div class="gl-card">
      <!-- Skeleton Table -->
      <template v-if="loading">
        <div class="gl-tbl-skel">
          <div class="gl-tbl-skel-head">
            <div class="gl-skel gl-skel-md"></div>
            <div class="gl-skel gl-skel-lg"></div>
            <div class="gl-skel gl-skel-sm"></div>
            <div class="gl-skel gl-skel-md"></div>
            <div class="gl-skel gl-skel-sm"></div>
            <div class="gl-skel gl-skel-md"></div>
          </div>
          <div v-for="i in 7" :key="i" class="gl-tbl-skel-row">
            <div class="gl-skel gl-skel-md" style="flex:1.5"></div>
            <div class="gl-skel gl-skel-sm" style="flex:1"></div>
            <div class="gl-skel gl-skel-sm" style="flex:0.7"></div>
            <div class="gl-skel gl-skel-md" style="flex:1.2"></div>
            <div class="gl-skel-badge"></div>
            <div style="display:flex;gap:6px">
              <div class="gl-skel-btn"></div>
              <div class="gl-skel-btn"></div>
              <div class="gl-skel-btn"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty State -->
      <div v-else-if="promos.length === 0" class="gl-empty">
        <div class="gl-empty-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
        </div>
        <div class="gl-empty-title">No promo codes found</div>
        <div class="gl-empty-sub">Create your first promo code to get started</div>
        <router-link to="/super-admin/promos/create" class="gl-btn-primary" style="margin-top:16px">Create Promo Code</router-link>
      </div>

      <template v-else>
        <!-- Desktop Table -->
        <div class="gl-tbl-scroll">
          <table class="gl-tbl">
            <thead>
              <tr>
                <th>Code / Name</th>
                <th>Discount</th>
                <th>Usage</th>
                <th>Validity</th>
                <th>Status</th>
                <th style="text-align:center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="promo in promos" :key="promo.id">
                <td>
                  <div class="gl-promo-code">{{ promo.code }}</div>
                  <div class="gl-tbl-sub">{{ promo.name }}</div>
                </td>
                <td>
                  <span class="gl-badge" :class="promo.discount_type === 'percentage' ? 'gl-badge-info' : 'gl-badge-green'">
                    {{ promo.formatted_discount || formatDiscount(promo) }}
                  </span>
                  <div v-if="promo.min_order_amount" class="gl-tbl-sub" style="margin-top:4px">Min: RM{{ formatCurrency(promo.min_order_amount) }}</div>
                  <div v-if="promo.max_discount_amount && promo.discount_type === 'percentage'" class="gl-tbl-sub">Cap: RM{{ formatCurrency(promo.max_discount_amount) }}</div>
                </td>
                <td>
                  <span class="gl-usage-count">{{ promo.times_used || 0 }}</span>
                  <span v-if="promo.usage_limit" class="gl-tbl-sub"> / {{ promo.usage_limit }}</span>
                  <span v-else class="gl-tbl-sub"> uses</span>
                  <div v-if="promo.usage_limit_per_user" class="gl-tbl-sub">{{ promo.usage_limit_per_user }}x per user</div>
                </td>
                <td>
                  <template v-if="promo.starts_at || promo.expires_at">
                    <div v-if="promo.starts_at" class="gl-tbl-sub">From: {{ formatDate(promo.starts_at) }}</div>
                    <div v-if="promo.expires_at" class="gl-tbl-sub">Until: {{ formatDate(promo.expires_at) }}</div>
                  </template>
                  <span v-else class="gl-tbl-sub">No expiry</span>
                </td>
                <td>
                  <span class="gl-badge" :class="getStatusBadgeClass(promo.computed_status || getComputedStatus(promo))">
                    {{ promo.computed_status || getComputedStatus(promo) }}
                  </span>
                </td>
                <td>
                  <div class="gl-tbl-actions">
                    <button
                      class="gl-icon-btn"
                      :class="promo.is_active ? 'gl-icon-btn-warn' : 'gl-icon-btn-success'"
                      :disabled="togglingId === promo.id"
                      :title="promo.is_active ? 'Deactivate' : 'Activate'"
                      @click="toggleStatus(promo)"
                    >
                      <span v-if="togglingId === promo.id" class="gl-spin"></span>
                      <template v-else>
                        <svg v-if="promo.is_active" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="5" width="22" height="14" rx="7"/><circle cx="16" cy="12" r="3" fill="currentColor"/></svg>
                        <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="5" width="22" height="14" rx="7"/><circle cx="8" cy="12" r="3" fill="currentColor"/></svg>
                      </template>
                    </button>
                    <router-link :to="`/super-admin/promos/${promo.id}/edit`" class="gl-icon-btn gl-icon-btn-primary" title="Edit">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </router-link>
                    <button class="gl-icon-btn gl-icon-btn-danger" title="Delete" @click="confirmDelete(promo)">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Cards -->
        <div class="gl-mob-list">
          <div v-for="promo in promos" :key="promo.id" class="gl-mob-card">
            <div class="gl-promo-mob-top">
              <div class="gl-promo-mob-left">
                <div class="gl-promo-code">{{ promo.code }}</div>
                <div class="gl-tbl-sub">{{ promo.name }}</div>
              </div>
              <span class="gl-badge" :class="getStatusBadgeClass(promo.computed_status || getComputedStatus(promo))">
                {{ promo.computed_status || getComputedStatus(promo) }}
              </span>
            </div>

            <div class="gl-promo-mob-grid">
              <div class="gl-promo-mob-cell">
                <div class="gl-mob-label">Discount</div>
                <span class="gl-badge" :class="promo.discount_type === 'percentage' ? 'gl-badge-info' : 'gl-badge-green'">
                  {{ promo.formatted_discount || formatDiscount(promo) }}
                </span>
                <div v-if="promo.min_order_amount" class="gl-tbl-sub" style="margin-top:3px">Min: RM{{ formatCurrency(promo.min_order_amount) }}</div>
              </div>
              <div class="gl-promo-mob-cell">
                <div class="gl-mob-label">Usage</div>
                <div class="gl-promo-usage-val">
                  <span class="gl-usage-count">{{ promo.times_used || 0 }}</span>
                  <span v-if="promo.usage_limit" class="gl-tbl-sub"> / {{ promo.usage_limit }}</span>
                  <span v-else class="gl-tbl-sub"> uses</span>
                </div>
                <div v-if="promo.usage_limit_per_user" class="gl-tbl-sub">{{ promo.usage_limit_per_user }}x per user</div>
              </div>
              <div class="gl-promo-mob-cell gl-promo-mob-full">
                <div class="gl-mob-label">Validity</div>
                <template v-if="promo.starts_at || promo.expires_at">
                  <div v-if="promo.starts_at" class="gl-tbl-sub">From: {{ formatDate(promo.starts_at) }}</div>
                  <div v-if="promo.expires_at" class="gl-tbl-sub">Until: {{ formatDate(promo.expires_at) }}</div>
                </template>
                <div v-else class="gl-tbl-sub">No expiry</div>
              </div>
            </div>

            <div class="gl-mob-actions">
              <button
                class="gl-mob-action-btn"
                :class="promo.is_active ? 'gl-mob-action-warn' : 'gl-mob-action-success'"
                :disabled="togglingId === promo.id"
                @click="toggleStatus(promo)"
              >
                <span v-if="togglingId === promo.id" class="gl-spin"></span>
                <template v-else>{{ promo.is_active ? 'Deactivate' : 'Activate' }}</template>
              </button>
              <router-link :to="`/super-admin/promos/${promo.id}/edit`" class="gl-mob-action-btn gl-mob-action-primary">Edit</router-link>
              <button class="gl-mob-action-btn gl-mob-action-danger" @click="confirmDelete(promo)">Delete</button>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.last_page > 1" class="gl-pager">
          <div class="gl-pager-info">
            Showing {{ (pagination.current_page - 1) * pagination.per_page + 1 }}–{{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }} of {{ pagination.total }}
          </div>
          <div class="gl-pager-btns">
            <button class="gl-pager-btn" :disabled="pagination.current_page === 1" @click="fetchPromos(pagination.current_page - 1)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <button
              v-for="page in paginationPages"
              :key="page"
              class="gl-pager-btn"
              :class="{ 'gl-pager-btn-active': page === pagination.current_page }"
              @click="fetchPromos(page)"
            >{{ page }}</button>
            <button class="gl-pager-btn" :disabled="pagination.current_page === pagination.last_page" @click="fetchPromos(pagination.current_page + 1)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- Delete Modal -->
    <div v-if="showDeleteModal" class="gl-modal-overlay" @click.self="showDeleteModal = false">
      <div class="gl-modal gl-modal-sm">
        <div class="gl-modal-header">
          <div>
            <div class="gl-modal-title">Delete Promo Code</div>
            <div v-if="selectedPromo" class="gl-modal-sub">{{ selectedPromo.code }}</div>
          </div>
          <button class="gl-modal-close" @click="showDeleteModal = false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <p style="margin:0 0 12px;color:#374151">
            Are you sure you want to delete <strong>{{ selectedPromo?.code }}</strong>? This action cannot be undone.
          </p>
          <div v-if="selectedPromo?.times_used > 0" class="gl-warn-strip">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            This code has been used {{ selectedPromo.times_used }} time(s).
          </div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-ghost" @click="showDeleteModal = false">Cancel</button>
          <button class="gl-btn-danger" @click="deletePromo" :disabled="deleting">
            <span v-if="deleting" class="gl-spin"></span>
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { superAdminPromoApi } from '@/services/superAdminApi';

export default {
  name: 'SAPromoList',
  data() {
    return {
      loading: true,
      deleting: false,
      togglingId: null,
      promos: [],
      stats: null,
      selectedPromo: null,
      showDeleteModal: false,
      filters: { search: '', status: '', discount_type: '' },
      pagination: { current_page: 1, last_page: 1, per_page: 20, total: 0 },
      debounceTimer: null
    };
  },
  computed: {
    paginationPages() {
      const pages = [];
      const start = Math.max(1, this.pagination.current_page - 2);
      const end = Math.min(this.pagination.last_page, this.pagination.current_page + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  created() {
    this.fetchStats();
    this.fetchPromos();
  },
  methods: {
    async fetchStats() {
      try {
        const response = await superAdminPromoApi.getStats();
        if (response.data.success) this.stats = response.data.data;
      } catch (e) { console.error(e); }
    },
    async fetchPromos(page = 1) {
      try {
        this.loading = true;
        const params = { page, per_page: this.pagination.per_page, ...this.filters };
        Object.keys(params).forEach(k => { if (params[k] === '' || params[k] === null) delete params[k]; });
        const response = await superAdminPromoApi.list(params);
        if (response.data.success) {
          this.promos = response.data.data;
          if (response.data.meta) this.pagination = response.data.meta;
        }
      } catch (e) { console.error(e); this.promos = []; }
      finally { this.loading = false; }
    },
    debouncedFetch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.fetchPromos(), 300);
    },
    resetFilters() {
      this.filters = { search: '', status: '', discount_type: '' };
      this.fetchPromos();
    },
    formatCurrency(value) {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    formatDiscount(promo) {
      return promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `RM ${this.formatCurrency(promo.discount_value)}`;
    },
    getComputedStatus(promo) {
      if (!promo.is_active) return 'inactive';
      const now = new Date();
      if (promo.starts_at && new Date(promo.starts_at) > now) return 'scheduled';
      if (promo.expires_at && new Date(promo.expires_at) < now) return 'expired';
      if (promo.usage_limit && promo.times_used >= promo.usage_limit) return 'exhausted';
      return 'active';
    },
    getStatusBadgeClass(status) {
      switch (status?.toLowerCase()) {
        case 'active': return 'gl-badge-green';
        case 'inactive': return 'gl-badge-gray';
        case 'expired': return 'gl-badge-red';
        case 'scheduled': return 'gl-badge-yellow';
        case 'exhausted': return 'gl-badge-dark';
        default: return 'gl-badge-gray';
      }
    },
    async toggleStatus(promo) {
      try {
        this.togglingId = promo.id;
        const response = await superAdminPromoApi.toggleStatus(promo.id);
        if (response.data.success) {
          const index = this.promos.findIndex(p => p.id === promo.id);
          if (index !== -1) {
            this.promos[index].is_active = response.data.data.is_active;
            this.promos[index].computed_status = response.data.data.status;
          }
          this.fetchStats();
        }
      } catch (e) {
        console.error(e);
        alert(e.response?.data?.message || 'Failed to toggle promo status');
      } finally { this.togglingId = null; }
    },
    confirmDelete(promo) {
      this.selectedPromo = promo;
      this.showDeleteModal = true;
    },
    async deletePromo() {
      if (!this.selectedPromo) return;
      try {
        this.deleting = true;
        await superAdminPromoApi.delete(this.selectedPromo.id);
        this.showDeleteModal = false;
        this.fetchPromos(this.pagination.current_page);
        this.fetchStats();
      } catch (e) {
        console.error(e);
        alert(e.response?.data?.message || 'Failed to delete promo code');
      } finally { this.deleting = false; this.selectedPromo = null; }
    }
  }
};
</script>

<style>
/* ── GL Page shell ── */
.gl-page { padding: 24px; max-width: 1400px; }
.gl-page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; gap: 16px; }
.gl-page-header-left {}
.gl-page-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0 0 2px; letter-spacing: -.3px; }
.gl-page-sub { font-size: 13px; color: #6b7f7b; margin: 0; }
.gl-page-header-right { flex-shrink: 0; }

/* ── Buttons ── */
.gl-btn-primary { display: inline-flex; align-items: center; gap: 6px; background: #900C27; color: #fff; border: none; border-radius: 10px; padding: 9px 16px; font-size: 13.5px; font-weight: 600; cursor: pointer; text-decoration: none; transition: background .15s; }
.gl-btn-primary:hover { background: #1a5048; color: #fff; }
.gl-btn-ghost { background: transparent; border: 1.5px solid #e0e7e5; color: #374151; border-radius: 9px; padding: 9px 18px; font-size: 13.5px; font-weight: 500; cursor: pointer; }
.gl-btn-danger { background: #dc2626; color: #fff; border: none; border-radius: 9px; padding: 9px 18px; font-size: 13.5px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }

/* ── Stats grid ── */
.gl-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 20px; }
.gl-stat-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 18px 20px; display: flex; align-items: center; gap: 14px; }
.gl-stat-icon-wrap { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-stat-body { flex: 1; min-width: 0; }
.gl-stat-label { font-size: 11.5px; color: #6b7f7b; font-weight: 500; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 4px; }
.gl-stat-value { font-size: 22px; font-weight: 700; color: #111827; letter-spacing: -.5px; line-height: 1; }

/* ── Filter bar ── */
.gl-filter-bar { display: flex; gap: 10px; align-items: center; margin-bottom: 16px; flex-wrap: wrap; }
.gl-filter-search { position: relative; flex: 1; min-width: 200px; }
.gl-filter-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; pointer-events: none; }
.gl-filter-input { width: 100%; padding: 9px 12px 9px 36px; border: 1.5px solid #e0e7e5; border-radius: 10px; font-size: 13.5px; color: #111827; background: #fff; outline: none; box-sizing: border-box; }
.gl-filter-input:focus { border-color: #900C27; }
.gl-filter-select { padding: 9px 12px; border: 1.5px solid #e0e7e5; border-radius: 10px; font-size: 13.5px; color: #111827; background: #fff; outline: none; cursor: pointer; }
.gl-filter-select:focus { border-color: #900C27; }
.gl-filter-clear { display: inline-flex; align-items: center; gap: 5px; padding: 9px 14px; border: 1.5px solid #e0e7e5; border-radius: 10px; background: #fff; color: #6b7f7b; font-size: 13px; cursor: pointer; white-space: nowrap; }
.gl-filter-clear:hover { border-color: #dc2626; color: #dc2626; }

/* ── Card ── */
.gl-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; }

/* ── Table ── */
.gl-tbl-scroll { overflow-x: auto; }
.gl-tbl { width: 100%; border-collapse: collapse; }
.gl-tbl th { padding: 12px 16px; font-size: 11px; font-weight: 600; color: #6b7f7b; text-transform: uppercase; letter-spacing: .6px; background: #f8faf9; border-bottom: 1px solid #eaedec; white-space: nowrap; }
.gl-tbl td { padding: 14px 16px; font-size: 13.5px; color: #111827; border-bottom: 1px solid #f5f7f6; vertical-align: middle; }
.gl-tbl tbody tr:last-child td { border-bottom: none; }
.gl-tbl tbody tr:hover td { background: #f8faf9; }
.gl-tbl-sub { font-size: 11.5px; color: #9ca3af; margin-top: 1px; }
.gl-tbl-actions { display: flex; gap: 6px; align-items: center; justify-content: center; }

/* ── Promo code display ── */
.gl-promo-code { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 13px; font-weight: 700; color: #900C27; letter-spacing: .5px; background: #e8f4f1; padding: 2px 7px; border-radius: 5px; display: inline-block; }
.gl-usage-count { font-size: 14px; font-weight: 700; color: #111827; }

/* ── Badges ── */
.gl-badge { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 20px; font-size: 11.5px; font-weight: 600; text-transform: capitalize; white-space: nowrap; }
.gl-badge-green { background: #dcfce7; color: #15803d; }
.gl-badge-gray { background: #f1f5f9; color: #64748b; }
.gl-badge-red { background: #fee2e2; color: #dc2626; }
.gl-badge-yellow { background: #fef9c3; color: #92400e; }
.gl-badge-info { background: #dbeafe; color: #1d4ed8; }
.gl-badge-dark { background: #1f2937; color: #f9fafb; }

/* ── Icon buttons ── */
.gl-icon-btn { width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid #e0e7e5; background: #fff; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: all .15s; color: #6b7f7b; text-decoration: none; }
.gl-icon-btn:hover { border-color: #900C27; color: #900C27; background: #e8f4f1; }
.gl-icon-btn-primary:hover { border-color: #2563eb; color: #2563eb; background: #eff6ff; }
.gl-icon-btn-danger:hover { border-color: #dc2626; color: #dc2626; background: #fef2f2; }
.gl-icon-btn-warn:hover { border-color: #d97706; color: #d97706; background: #fffbeb; }
.gl-icon-btn-success:hover { border-color: #16a34a; color: #16a34a; background: #f0fdf4; }
.gl-icon-btn:disabled { opacity: .5; cursor: not-allowed; }

/* ── Pagination ── */
.gl-pager { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-top: 1px solid #f0f3f2; flex-wrap: wrap; gap: 10px; }
.gl-pager-info { font-size: 12.5px; color: #9ca3af; }
.gl-pager-btns { display: flex; gap: 4px; }
.gl-pager-btn { min-width: 32px; height: 32px; padding: 0 8px; border: 1.5px solid #e0e7e5; border-radius: 8px; background: #fff; font-size: 13px; color: #374151; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.gl-pager-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; }
.gl-pager-btn:disabled { opacity: .4; cursor: not-allowed; }
.gl-pager-btn-active { background: #900C27; border-color: #900C27; color: #fff !important; font-weight: 600; }

/* ── Empty state ── */
.gl-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px 24px; }
.gl-empty-icon { width: 64px; height: 64px; border-radius: 16px; background: #f0f3f2; display: flex; align-items: center; justify-content: center; color: #9ca3af; margin-bottom: 16px; }
.gl-empty-title { font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 4px; }
.gl-empty-sub { font-size: 13px; color: #9ca3af; }

/* ── Skeleton ── */
.gl-skel { background: linear-gradient(90deg, #f0f3f2 25%, #e8edeb 50%, #f0f3f2 75%); background-size: 200% 100%; border-radius: 6px; animation: gl-shimmer 1.5s infinite; display: block; }
@keyframes gl-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.gl-skel-sm { width: 60px; height: 10px; }
.gl-skel-md { width: 100px; height: 10px; }
.gl-skel-lg { width: 140px; height: 10px; }
.gl-skel-badge { width: 60px; height: 22px; border-radius: 20px; background: #f0f3f2; animation: gl-shimmer 1.5s infinite; }
.gl-skel-btn { width: 32px; height: 32px; border-radius: 8px; background: #f0f3f2; animation: gl-shimmer 1.5s infinite; }
.gl-tbl-skel { padding: 0 4px; }
.gl-tbl-skel-head { display: flex; gap: 20px; padding: 14px 16px; border-bottom: 1px solid #eaedec; }
.gl-tbl-skel-row { display: flex; align-items: center; gap: 20px; padding: 16px; border-bottom: 1px solid #f5f7f6; }

/* ── Spinner ── */
.gl-spin { display: inline-block; width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.3); border-top-color: currentColor; border-radius: 50%; animation: gl-spin .6s linear infinite; }
@keyframes gl-spin { to { transform: rotate(360deg); } }

/* ── Modal ── */
.gl-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1050; padding: 16px; }
.gl-modal { background: #fff; border-radius: 18px; width: 100%; max-width: 440px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,.18); position: relative; }
.gl-modal-sm { max-width: 420px; }
.gl-modal-header { padding: 22px 56px 16px 24px; border-bottom: 1px solid #f0f3f2; }
.gl-modal-title { font-size: 16px; font-weight: 700; color: #111827; }
.gl-modal-sub { font-size: 12.5px; color: #6b7f7b; margin-top: 2px; font-family: 'SF Mono', monospace; font-weight: 600; color: #900C27; }
.gl-modal-close { position: absolute; top: 16px; right: 16px; width: 32px; height: 32px; border-radius: 8px; border: 1px solid #eaedec; background: #fff; color: #6b7f7b; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.gl-modal-close:hover { background: #f8faf9; }
.gl-modal-body { padding: 20px 24px; }
.gl-modal-footer { padding: 16px 24px; border-top: 1px solid #f0f3f2; display: flex; justify-content: flex-end; gap: 10px; }

/* ── Warning strip ── */
.gl-warn-strip { display: flex; align-items: center; gap: 8px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 9px; padding: 10px 14px; font-size: 12.5px; color: #92400e; }

/* ── Mobile cards ── */
.gl-mob-list { display: none; flex-direction: column; gap: 0; }
.gl-mob-card { padding: 16px; border-bottom: 1px solid #f0f3f2; }
.gl-mob-card:last-child { border-bottom: none; }
.gl-promo-mob-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
.gl-promo-mob-left { min-width: 0; }
.gl-promo-mob-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; }
.gl-promo-mob-cell {}
.gl-promo-mob-full { grid-column: 1 / -1; }
.gl-mob-label { font-size: 10.5px; color: #9ca3af; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 4px; }
.gl-promo-usage-val { display: flex; align-items: baseline; gap: 2px; }
.gl-mob-actions { display: flex; gap: 8px; }
.gl-mob-action-btn { flex: 1; padding: 8px 6px; border-radius: 9px; border: 1.5px solid #e0e7e5; background: #fff; font-size: 12.5px; font-weight: 600; cursor: pointer; text-align: center; text-decoration: none; color: #374151; display: flex; align-items: center; justify-content: center; gap: 4px; }
.gl-mob-action-primary { border-color: #2563eb; color: #2563eb; background: #eff6ff; }
.gl-mob-action-danger { border-color: #dc2626; color: #dc2626; background: #fef2f2; }
.gl-mob-action-warn { border-color: #d97706; color: #d97706; background: #fffbeb; }
.gl-mob-action-success { border-color: #16a34a; color: #16a34a; background: #f0fdf4; }
.gl-mob-action-btn:disabled { opacity: .5; cursor: not-allowed; }

/* ── Responsive ── */
@media (max-width: 767px) {
  .gl-page { padding: 16px; }
  .gl-page-title { font-size: 18px; }
  .gl-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .gl-stat-card { padding: 14px; gap: 10px; }
  .gl-stat-value { font-size: 18px; }
  .gl-filter-bar { flex-direction: column; align-items: stretch; }
  .gl-filter-select { width: 100%; }
  .gl-filter-clear { width: 100%; justify-content: center; }
  .gl-tbl-scroll { display: none; }
  .gl-mob-list { display: flex; }
  .gl-pager { flex-direction: column; align-items: center; gap: 10px; padding: 14px; }
  .gl-modal { border-radius: 20px 20px 0 0; max-width: 100%; margin: auto 0 0; max-height: 85vh; }
  .gl-modal-overlay { align-items: flex-end; padding: 0; }
}

/* ── Dark mode ── */
body.dark-only .gl-page-title { color: #f1f5f9; }
body.dark-only .gl-page-sub { color: #94a3b8; }
body.dark-only .gl-stat-card,
body.dark-only .gl-card,
body.dark-only .gl-modal { background: #1e2a27; border-color: #2d3f3b; }
body.dark-only .gl-stat-value { color: #f1f5f9; }
body.dark-only .gl-tbl th { background: #162220; color: #94a3b8; border-color: #2d3f3b; }
body.dark-only .gl-tbl td { color: #e2e8f0; border-color: #1e2a27; }
body.dark-only .gl-tbl tbody tr:hover td { background: #162220; }
body.dark-only .gl-tbl-sub { color: #64748b; }
body.dark-only .gl-filter-input,
body.dark-only .gl-filter-select { background: #1e2a27; border-color: #2d3f3b; color: #e2e8f0; }
body.dark-only .gl-filter-clear { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-icon-btn { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-pager-btn { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-mob-card { border-color: #2d3f3b; }
body.dark-only .gl-mob-action-btn { background: #162220; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-modal-header { border-color: #2d3f3b; }
body.dark-only .gl-modal-title { color: #f1f5f9; }
body.dark-only .gl-modal-footer { border-color: #2d3f3b; }
body.dark-only .gl-modal-close { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-btn-ghost { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-promo-code { background: #162220; color: #4ade80; }
body.dark-only .gl-empty-icon { background: #162220; }
body.dark-only .gl-empty-title { color: #e2e8f0; }
body.dark-only .gl-badge-green { background: rgba(22,163,74,.22); color: #4ade80; }
body.dark-only .gl-badge-gray { background: rgba(100,116,139,.2); color: #94a3b8; }
body.dark-only .gl-badge-red { background: rgba(220,38,38,.22); color: #f87171; }
body.dark-only .gl-badge-yellow { background: rgba(217,119,6,.22); color: #fbbf24; }
body.dark-only .gl-badge-info { background: rgba(37,99,235,.22); color: #60a5fa; }
body.dark-only .gl-badge-dark { background: rgba(241,245,249,.12); color: #e2e8f0; }
</style>
